import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.#handleChange);
    this.addEventListener('click', this.#handleConnectedProductClick);
  }

  /**
   * Routes 'change' events to the sibling dropdown switch or the regular variant change handler.
   * @param {Event} event
   */
  #handleChange = (event) => {
    const target = event.target;

    if (target instanceof HTMLSelectElement && target.dataset.siblingSelect === 'true') {
      const option = target.selectedOptions[0];

      if (option) {
        this.switchSiblingProduct({
          newUrl: option.dataset.connectedProductUrl,
          optionValueId: option.dataset.optionValueId,
        });
      }

      return;
    }

    this.variantChanged(event);
  };

  /**
   * Buttons that switch to a different connected product (e.g. sibling products) aren't real
   * form inputs, so they don't emit a native 'change' event. Handle clicks on them the same way.
   * @param {MouseEvent} event
   */
  #handleConnectedProductClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target)?.closest('button[data-connected-product-url]');

    if (!(target instanceof HTMLButtonElement) || target.disabled) return;

    if (target.dataset.siblingSwitch === 'true') {
      this.switchSiblingProduct({ newUrl: target.dataset.connectedProductUrl, optionValueId: target.dataset.optionValueId });
    } else {
      this.variantChanged({ target });
    }
  };

  /**
   * Switches to a sibling product by fetching its page and letting the gallery and product form
   * update themselves via the dispatched event below. Only the sibling list's "current selection"
   * indicator is updated (see #markSiblingSelected) — everything else (title, description, price,
   * reviews, the sibling labels/order/images, etc.) is intentionally left untouched, and the
   * URL/page title never change.
   * @param {Object} options
   * @param {string | undefined} options.newUrl
   * @param {string | undefined} options.optionValueId
   */
  async switchSiblingProduct({ newUrl, optionValueId }) {
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const currentSection = this.closest('.product-information');

    if (!newUrl || newUrl === currentUrl || !currentSection) return;

    this.dispatchEvent(new VariantSelectedEvent({ id: optionValueId ?? '' }));

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const requestUrl = new URL(newUrl, window.location.origin);
    const activeView = new URL(window.location.href).searchParams.get('view');
    if (activeView) requestUrl.searchParams.set('view', activeView);

    try {
      const response = await fetch(`${requestUrl.pathname}${requestUrl.search}`, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) return;

      const responseText = await response.text();
      this.#pendingRequestUrl = undefined;
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

      const newVariantPickerSource = html.querySelector('variant-picker');
      const textContent = newVariantPickerSource?.querySelector('script[type="application/json"]')?.textContent;

      if (!textContent) return;

      const previousProductId = this.dataset.productId;

      // Only track the new product id/url for subsequent clicks (e.g. switching back) — the
      // sibling buttons/select themselves are never morphed, so they keep looking exactly as
      // they did when the page loaded.
      const newProductId =
        newVariantPickerSource instanceof HTMLElement ? newVariantPickerSource.dataset.productId : undefined;
      const newProductUrl =
        newVariantPickerSource instanceof HTMLElement ? newVariantPickerSource.dataset.productUrl : undefined;

      if (newProductId) this.dataset.productId = newProductId;
      if (newProductUrl) this.dataset.productUrl = newProductUrl;

      this.#markSiblingSelected(newUrl);

      const newProduct =
        newProductId && newProductUrl && previousProductId !== newProductId
          ? { id: newProductId, url: newProductUrl }
          : undefined;

      // Sections outside of this one (e.g. the sticky add-to-cart bar, media-gallery, product form)
      // rely on this event to sync. preserveContent tells unrelated listeners (price, inventory,
      // local pickup, etc.) to skip so their content stays as it was for this product.
      this.dispatchEvent(
        new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId ?? previousProductId ?? '', {
          html,
          productId: previousProductId ?? '',
          newProduct,
          preserveContent: true,
        })
      );
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
    }
  }

  /**
   * Updates which sibling button/option is marked as the current selection, without touching
   * anything else in the sibling list (labels, order, images, etc. stay exactly as rendered).
   * @param {string} newUrl
   */
  #markSiblingSelected(newUrl) {
    this.querySelectorAll('.sibling-products__thumb').forEach((thumb) => {
      if (!(thumb instanceof HTMLButtonElement)) return;

      const isSelected = thumb.dataset.connectedProductUrl === newUrl;

      thumb.classList.toggle('active', isSelected);
      thumb.disabled = isSelected;

      if (isSelected) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    const select = this.querySelector('select[data-sibling-select="true"]');
    if (select instanceof HTMLSelectElement) select.value = newUrl;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    // If inside sticky ATC, don't fetch — sticky-atc.js delegates to the main picker instead
    if (this.closest('sticky-atc-component')) return;

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // Same product, same page: only this section's HTML is needed, so request it via the Section
    // Rendering API instead of downloading and parsing the entire page.
    const sectionId = loadsNewProduct ? undefined : this.dataset.sectionId;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target, null, [], sectionId), loadsNewProduct);

    const url = new URL(window.location.href);

    let variantId;

    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      variantId = event.target.dataset.variantId || null;
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {string} [sectionId] - When provided, scopes the request to a single section via the Section Rendering API.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], sectionId = undefined) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = new URLSearchParams();

    if (this.selectedOptionsValues.length && !source) {
      params.set('option_values', this.selectedOptionsValues.join(','));
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.set('option_values', sourceSelectedOptionsValues.join(','));
      } else {
        params.set('option_values', selectedOption.dataset.optionValueId ?? '');
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.toString()}`;
    }

    const requestUrl = new URL(productUrl, window.location.origin);
    const activeView = new URL(window.location.href).searchParams.get('view');
    if (activeView && !requestUrl.searchParams.has('view')) {
      requestUrl.searchParams.set('view', activeView);
    }
    params.forEach((value, key) => requestUrl.searchParams.set(key, value));
    if (sectionId) {
      requestUrl.searchParams.set('section_id', sectionId);
    }
    return `${requestUrl.pathname}${requestUrl.search}${requestUrl.hash}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          const previousProductId = this.dataset.productId;

          this.updateMain(html);

          // updateMain morphs <main> in place, so this.dataset.productId reflects the new product afterwards.
          const newProduct =
            previousProductId !== this.dataset.productId
              ? { id: this.dataset.productId ?? '', url: this.dataset.productUrl ?? '' }
              : undefined;

          // Sections outside of <main> (e.g. the sticky add-to-cart bar) don't get morphed above,
          // so they rely on this event to stay in sync with the newly loaded product.
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId ?? previousProductId ?? '', {
              html,
              productId: previousProductId ?? '',
              newProduct,
            })
          );
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
